import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { tokens } from "../theme";
import type { SignageScreenGroup } from "@shared/types/signage";
import { PermissionGate } from "../auth/access";

/**
 * Confirm deleting a group.
 *
 * ## Why it spells out what is NOT deleted
 *
 * "Delete group" sits one click from a list of screens, and the two words an
 * operator is weighing are "group" and "screens". Only one of those is
 * recoverable: a group is a name and a list of ids and can be rebuilt in a
 * minute; a screen is a paired television in a building. Saying "the screens
 * are not deleted" out loud costs one line and removes the hesitation that
 * otherwise makes people leave dead groups lying around forever.
 *
 * ## Why it counts the screens
 *
 * A group with 8 screens in it is a different decision from an empty one, and
 * the count is the only part of that an operator cannot see from the row they
 * clicked.
 */
export default function SignageGroupDeleteDialog({
  group,
  busy,
  onClose,
  onConfirm,
}: {
  /** The group to delete, or null when the dialog is closed. */
  group: SignageScreenGroup | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation(["signage", "common"]);
  const count = group?.screenIds.length ?? 0;
  return (
    <Dialog open={group !== null} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" gap={1}>
          <Typography
            sx={{ flex: 1, typography: "title", fontWeight: "fontWeightBold" }}
          >
            {t("signageGroupDeleteDialog.title")}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label={t("common:buttons.close")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          <Trans
            t={t}
            i18nKey="signageGroupDeleteDialog.willBeDeleted"
            values={{ name: group?.name }}
            components={{ 1: <b style={{ color: tokens.ink }} /> }}
          />
        </Typography>

        <Box component="ul" sx={{ pl: 2.5, my: 2 }}>
          <Typography
            variant="body2"
            component="li"
            sx={{ color: tokens.inkMuted }}
          >
            {t("signageGroupDeleteDialog.affected", { count })}
          </Typography>
          <Typography
            variant="body2"
            component="li"
            sx={{ color: tokens.inkMuted }}
          >
            {t("signageGroupDeleteDialog.screensNotDeleted")}
          </Typography>
        </Box>

        <Stack direction="row" alignItems="center" gap={1}>
          <WarningAmberIcon sx={{ fontSize: 20, color: tokens.error }} />
          <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
            {t("common:labels.cannotBeUndone")}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
        <Box sx={{ flex: 1 }} />
        <PermissionGate
          resource="signage"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <Button
            variant="contained"
            color="error"
            disabled={busy}
            onClick={onConfirm}
          >
            {busy
              ? t("common:states.deleting")
              : t("signageGroupDeleteDialog.deleteGroup")}
          </Button>
        </PermissionGate>
      </DialogActions>
    </Dialog>
  );
}
